<template>
    <div class="section__title__box">
        <h4 class="title">Order Management</h4>
        <div class="rlms-table-filters">
            <div class="scrollable-container">
                <div class="head__title__wrap">
                    <div class="title__select__box">
                        <select class="select" v-model="status">
                            <option value="">All</option>
                            <option value="0">Pending</option>
                            <option value="1">Paid</option>
                            <option value="2">PO Number</option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="rlms-tf-actions">
                <div class="input-icon-group input-icon-right">
                    <input type="search" placeholder="Search..." v-model.trim="search" @input="handleSearch">
                    <img class="rlms-input-icon" src="@/assets/images/search.svg">
                </div>
            </div>
        </div>
    </div>
    <div class="rlms-table" style="width: 100%;">
        <div class="rlms-table-inner">
            <table>
                <thead>
                    <tr>
                        <th>Purchase ID</th>
                        <th>User Email</th>
                        <th>Organization</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>PO</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-if="orders?.length > 0">
                        <tr v-for="order in orders" :key="order?.id">
                            <td>#{{ order?.id }}</td>
                            <td>{{ order?.user?.email }}</td>
                            <td>{{ order?.organization?.name }}</td>
                            <td>{{ order?.learner_quantity }}</td>
                            <td>{{ formatCurrency(order?.amount * order?.learner_quantity) }}</td>
                            <td>{{ formatDate(order?.created_at, 'MMM d, Y') }}</td>
                            <td>{{ order?.reference_num }}</td>
                            <td>
                                <div
                                    :class="{ active__label: order?.status === 1, inactive__label: (order?.status == 0 || order?.status == 2) }">
                                    {{ getStatusText(order?.status) }}</div>
                            </td>
                            <td>
                                <div class="action__tab">
                                    <div class="action__btn">
                                        <RouterLink :to="`/admin/orders/${order?.id}`" class="">
                                            <span class="btn__text">View</span>
                                            <span class="btn__arrow">
                                                <img src="@/assets/images/arrow-right.svg" alt="Arrow">
                                            </span>
                                        </RouterLink>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr>
                            <td colspan="8">
                                <div class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center">
                                    <img :src="noAssessmentsImg" class="rlms-mx-auto" style="width: 100px">
                                    <p v-if="search || status">No purchase orders found for the selected search or filter criteria.</p>
                                    <p v-else>No purchase orders available.</p>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
    <div class="rlms-w-100">
        <paginate v-model="page" :page-count="totalPages" :margin-pages="2" :prev-text="'<'" :next-text="'>'"
            :container-class="'pagination'" :page-class="'page-item'" :active-class="'active-page'" v-if="totalPages > 1" />
    </div>
</template>
<script setup>
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { debounce } from '@/utils/debounce';
import { formatCurrency, formatDate } from '@/utils/format-helpers';
import { handleError } from '@/utils/handleError';
import { onMounted, ref, watch } from 'vue';
import paginate from 'vuejs-paginate-next';
import noAssessmentsImg from '@/assets/images/no-assessments.png';

const page = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const status = ref("");
const search = ref("");

const orders = ref([]);
const loader = useLoaderStore();


const getOrders = async () => {
    try {
        loader.show();

        const { data } = await api.get(`admin/orders`, {
            params: {
                page: page.value,
                per_page: 10,
                status: status.value,
                search: search.value,
            }
        });

        orders.value = data?.data?.data || [];

        totalPages.value = data?.data?.last_page ?? 1;
        totalItems.value = data?.data?.total ?? 0;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const getStatusText = (status) => {
    switch (status) {
        case 0:
            return 'Pending'
        case 1:
            return 'Paid'
        case 2:
            return 'PO Number'
        default:
            return 'Unknown'
    }
}

onMounted(async () => await getOrders());

watch(page, () => getOrders());

watch([status], () => {
    page.value = 1
    getOrders()
});

const handleSearch = debounce(function () {
    page.value = 1;
    getOrders();
}, 400);

</script>